<template>
    <section class="internal-page">
        <internal-page-header
            eyebrow="Área do credor"
            title="Assembleias de credores"
            description="Consulte as datas, os detalhes e as opções de participação de cada assembleia."
        />
        <div class="internal-content assembly-content">
            <div class="assembly-intro" v-html="dataset.description"></div>

            <section class="assembly-directory" aria-labelledby="assembly-directory-title">
                <div class="assembly-directory__heading">
                    <div>
                        <span class="assembly-kicker">Agenda de assembleias</span>
                        <h2 id="assembly-directory-title">Próximos encontros</h2>
                    </div>
                    <p>
                        <strong>{{ dataset.count || dataset.generalMeetings.length }}</strong>
                        {{
                            (dataset.count || dataset.generalMeetings.length) === 1
                                ? 'assembleia encontrada'
                                : 'assembleias encontradas'
                        }}
                    </p>
                </div>

                <v-form class="assembly-filters" @submit.prevent="load">
                    <v-text-field
                        v-model="dataset.filter.client_name"
                        label="Buscar por devedora"
                        placeholder="Nome da empresa"
                        prepend-inner-icon="mdi-magnify"
                        outlined
                        dense
                        hide-details
                    />
                    <v-select
                        v-model="dataset.filter.district"
                        :items="dataset.districts"
                        label="Comarca"
                        item-value="id"
                        item-text="name"
                        outlined
                        dense
                        hide-details
                    />
                    <v-btn type="submit" color="primary" class="assembly-filters__submit">
                        Aplicar filtros
                        <v-icon right size="18">mdi-arrow-right</v-icon>
                    </v-btn>
                </v-form>
            </section>

            <page-state
                v-if="dataset.loading || loadError || !dataset.generalMeetings.length"
                :loading="dataset.loading"
                :error="loadError"
                title="Nenhuma assembleia encontrada"
                description="As assembleias disponíveis serão exibidas aqui. Você também pode tentar outros filtros."
                @retry="load"
            />

            <div v-else class="assembly-agenda">
                <article
                    v-for="(item, index) in dataset.generalMeetings"
                    :key="item.id || index"
                    :ref="`assembly-item-${item.id}`"
                    class="assembly-entry"
                    :class="{ 'assembly-entry--open': item.showAssembly }"
                >
                    <button
                        type="button"
                        class="assembly-entry__summary"
                        :aria-expanded="item.showAssembly ? 'true' : 'false'"
                        @click="toggleAnswer(index)"
                    >
                        <time class="assembly-date" :datetime="item.date">
                            <strong>{{ formatMeetingDate(item.date, 'day') }}</strong>
                            <span>{{ formatMeetingDate(item.date, 'month') }}</span>
                            <small>{{ formatMeetingDate(item.date, 'year') }}</small>
                        </time>

                        <span class="assembly-entry__content">
                            <span class="assembly-entry__client">{{ item.client.name }}</span>
                            <strong class="assembly-entry__title">{{ item.name }}</strong>
                            <span class="assembly-entry__meta">
                                <span>
                                    <v-icon size="16">mdi-clock-outline</v-icon>
                                    {{ formatMeetingDate(item.date, 'time') }}
                                </span>
                                <span>
                                    <v-icon size="16">mdi-map-marker-outline</v-icon>
                                    {{ districtName(item) }}
                                </span>
                                <span>
                                    <v-icon size="16">mdi-file-document-outline</v-icon>
                                    {{ item.client.process_number }}
                                </span>
                            </span>
                        </span>

                        <span class="assembly-entry__aside">
                            <span
                                class="assembly-status"
                                :class="`assembly-status--${item.status}`"
                            >
                                {{ statusName(item.status) }}
                            </span>
                            <span class="assembly-entry__toggle">
                                {{ item.showAssembly ? 'Recolher' : 'Ver detalhes' }}
                                <v-icon size="19">{{
                                    item.showAssembly ? 'mdi-chevron-up' : 'mdi-chevron-down'
                                }}</v-icon>
                            </span>
                        </span>
                    </button>

                    <v-expand-transition>
                        <div v-show="item.showAssembly" class="assembly-entry__details">
                            <span class="assembly-entry__details-label">Sobre a assembleia</span>
                            <div class="internal-rich-text" v-html="item.description"></div>
                        </div>
                    </v-expand-transition>
                </article>
            </div>

            <v-dialog v-if="dataset.selectedNextGeneralMeeting" v-model="showModal" max-width="600">
                <v-card class="mx-auto" outlined>
                    <v-list-item three-line>
                        <v-list-item-content>
                            <div class="text-overline mb-4">Próxima Assembleia</div>
                            <v-list-item-title class="text-h7 text-lg-h8 mb-4">
                                A sua próxima assembléia será:
                            </v-list-item-title>
                            <v-list-item-title class="title-page">{{
                                dataset.selectedNextGeneralMeeting.date | defaultDate('DD/MM/YYYY')
                            }}</v-list-item-title>
                            <v-list-item-title class="font-weight-bold">{{
                                dataset.selectedNextGeneralMeeting.name
                            }}</v-list-item-title>
                            <v-list-item-title class="font-weight-normal">
                                <p>
                                    <span>
                                        <strong>Caso:</strong>
                                        {{ dataset.selectedNextGeneralMeeting.client_name }}
                                    </span>
                                </p>
                                <p>
                                    <span
                                        v-if="
                                            !dataset.selectedNextGeneralMeeting.has_accreditation &&
                                            dataset.selectedNextGeneralMeeting.status == 0
                                        "
                                        >Seu acesso ainda não foi concedido.
                                        <br />
                                        Para obter credenciamento,
                                        <a
                                            href="#"
                                            @click.prevent="
                                                openAccreditationPage(
                                                    dataset.selectedNextGeneralMeeting.id
                                                )
                                            "
                                            ><strong>clique aqui</strong></a
                                        >
                                    </span>
                                </p>
                                <p>
                                    <span>
                                        <v-chip
                                            x-small
                                            label
                                            outlined
                                            :color="
                                                generalMeetingStatusColor(
                                                    dataset.selectedNextGeneralMeeting.status
                                                )
                                            "
                                        >
                                            <strong>
                                                {{
                                                    dataset.selectedNextGeneralMeeting.status
                                                        | enum(
                                                            dataset.generalMeetingStatus,
                                                            'id',
                                                            'name'
                                                        )
                                                }}</strong
                                            ></v-chip
                                        >
                                    </span>
                                </p>
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-avatar tile size="100">
                            <v-img src="@/assets/background.jpg" class="small-image"></v-img>
                        </v-list-item-avatar>
                    </v-list-item>

                    <v-divider></v-divider>

                    <v-subheader v-if="dataset.nextGeneralMeetings.length"
                        >Eventos Futuros</v-subheader
                    >
                    <v-timeline
                        v-if="dataset.nextGeneralMeetings.length"
                        class="px-10 timeline-scroll-container"
                    >
                        <v-timeline-item
                            v-for="(item, index) in dataset.nextGeneralMeetings"
                            :key="index"
                            color="primary"
                            small
                        >
                            <div class="future-meeting-card">
                                <div>
                                    <strong>{{ item.date | defaultDate('DD/MM/YYYY') }}</strong>
                                </div>
                                <div>{{ item.name }}</div>
                                <div>
                                    <span>
                                        <v-chip
                                            x-small
                                            label
                                            outlined
                                            :color="generalMeetingStatusColor(item.status)"
                                        >
                                            <strong>
                                                {{
                                                    item.status
                                                        | enum(
                                                            dataset.generalMeetingStatus,
                                                            'id',
                                                            'name'
                                                        )
                                                }}</strong
                                            ></v-chip
                                        >
                                    </span>
                                </div>
                                <div class="mt-6"></div>
                                <div
                                    v-if="!item.has_accreditation && item.status == 0"
                                    class="mt-2"
                                >
                                    <span
                                        >Seu acesso ainda não foi concedido.
                                        <br />
                                        Para obter credenciamento,
                                        <a
                                            href="#"
                                            @click.prevent="
                                                openAccreditationPage(
                                                    dataset.selectedNextGeneralMeeting.id
                                                )
                                            "
                                            ><strong>clique aqui</strong></a
                                        >
                                    </span>
                                </div>
                                <div class="mb-6" v-if="item.has_accreditation">
                                    <span>
                                        <i>Você está credenciado para participar!</i>
                                    </span>
                                </div>
                                <div class="mt-2">
                                    <span class="caption font-weight-bold">{{
                                        item.client_name
                                    }}</span>
                                </div>
                            </div>
                        </v-timeline-item>
                    </v-timeline>

                    <v-card-actions>
                        <v-btn
                            outlined
                            rounded
                            text
                            class="primary white--text"
                            @click="closeModal"
                        >
                            Fechar
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </div>
    </section>
</template>

<script>
import 'moment/locale/pt-br';

import object from '@/common/util/object';
import axios from 'axios';

import generalMeetingStatus from '@/common/general-meeting-status.data';

export default {
    data() {
        return {
            user: null,
            dataset: {
                filter: {
                    client_name: '',
                    district: ''
                },
                generalMeetingStatus: generalMeetingStatus,
                description: '',
                generalMeetings: [],
                districts: [],
                states: [],
                options: {
                    page: 1,
                    itemsPerPage: 50,
                    sortBy: ['name'],
                    sortDesc: [false]
                },
                nextGeneralMeetings: [],
                selectedNextGeneralMeeting: null,
                count: 0,
                loading: false
            },
            loadError: false,
            showModal: false,
            modalTitle: '',
            modalDate: '',
            assemblyName: '',
            futureMeetings: []
        };
    },

    async mounted() {
        await this.loadData();

        const eventId = this.$route.query.eventId;
        if (eventId) {
            this.openSpecificCollapse(eventId);
        }
    },

    methods: {
        formatMeetingDate(value, part) {
            if (!value) return 'Não informado';

            const date = new Date(value);
            const options = {
                timeZone: 'America/Sao_Paulo'
            };

            if (part === 'day')
                return new Intl.DateTimeFormat('pt-BR', { ...options, day: '2-digit' }).format(
                    date
                );
            if (part === 'month')
                return new Intl.DateTimeFormat('pt-BR', { ...options, month: 'short' })
                    .format(date)
                    .replace('.', '')
                    .toUpperCase();
            if (part === 'year')
                return new Intl.DateTimeFormat('pt-BR', { ...options, year: 'numeric' }).format(
                    date
                );
            if (part === 'time')
                return new Intl.DateTimeFormat('pt-BR', {
                    ...options,
                    hour: '2-digit',
                    minute: '2-digit'
                }).format(date);

            return new Intl.DateTimeFormat('pt-BR', options).format(date);
        },

        districtName(item) {
            const district = this.dataset.districts.find(
                (entry) => entry.id === item.client?.district_id
            );
            return district?.name || 'Comarca não informada';
        },

        statusName(status) {
            return (
                this.dataset.generalMeetingStatus.find((item) => item.id === status)?.name ||
                'Status não informado'
            );
        },

        openSpecificCollapse(eventId) {
            const eventIndex = this.dataset.generalMeetings.findIndex(
                (event) => event.id == eventId
            );
            if (eventIndex !== -1) {
                this.$set(this.dataset.generalMeetings[eventIndex], 'showAssembly', true);
                this.scrollToEvent(eventId);
            }
        },

        // scrollToEvent(eventId) {
        //     this.$nextTick(() => {
        //         const assemblyItem = this.$refs[`assembly-item-${eventId}`];
        //         if (assemblyItem && assemblyItem[0]) {
        //             const scroller = new SweetScroll();
        //             scroller.toElement(assemblyItem[0]);
        //         }
        //     });
        // },

        openAccreditationPage(generalMeetingId) {
            this.$router.push({
                path: '/assembleia-credores/credenciamento',
                query: { id: generalMeetingId }
            });
        },

        getNextMeeting() {
            const today = new Date();
            const futureMeetings = this.dataset.generalMeetings
                .filter((meeting) => new Date(meeting.date) > today)
                .sort((a, b) => new Date(a.date) - new Date(b.date));

            return futureMeetings.length > 0 ? futureMeetings[0] : null;
        },

        generalMeetingStatusColor(status) {
            switch (status) {
                case 0:
                    return 'primary';
                case 1:
                    return 'primary';
                case 2:
                    return 'error';
                case 3:
                    return 'warning';
                case 4:
                    return 'grey';
                case 5:
                    return 'green';
                default:
                    break;
            }
        },

        async loadData() {
            if (this.$root.user) await this.loadNextGeneralMeetings();
            await this.loadText();
            await this.loadDistricts();
            await this.load();
        },

        toggleAnswer(index) {
            let showAssembly = this.dataset.generalMeetings[index].showAssembly;
            this.dataset.generalMeetings[index].showAssembly =
                showAssembly == undefined ? true : !showAssembly;
        },

        submitForm() {
            const emptyForm = Object.values(this.formData).every((value) => !value);

            if (emptyForm) {
                // this.openModal('Campo Obrigatório', 'Preencha ao menos um campo para pesquisa.');
                return;
            }

            this.clearForm();
        },

        clearForm() {
            this.formData = {
                find: '',
                comarca: '',
                uf: ''
            };
        },

        async loadDistricts() {
            try {
                const result = await axios.get(`/api/districts`, {
                    params: { start: 1, limit: 100000, search: {} }
                });

                this.dataset.districts = [{ id: '', name: 'Todas' }, ...result.data.content.data];
            } catch (error) {
                console.log('ERRO', error);
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                });
            }
        },

        async load() {
            let search = Object.fromEntries(
                Object.entries(this.dataset.filter).filter(([, value]) => value)
            );

            search = !object.isEmpty(search) ? search : null;

            this.dataset.loading = true;
            this.loadError = false;

            var limit = this.dataset.options.itemsPerPage;
            var start = (this.dataset.options.page - 1) * limit;

            try {
                const result = await axios.get('/api/general-meetings', {
                    params: {
                        start: start,
                        limit: limit,
                        search: search ? JSON.stringify(search) : null
                    }
                });

                const meetings = Array.isArray(result.data.content)
                    ? result.data.content
                    : result.data.content.data || [];
                this.dataset.generalMeetings = meetings.map((x) => {
                    return { ...x, showAssembly: false };
                });
                this.dataset.count = result.data.count;
            } catch (error) {
                console.log('error ', error);
                this.loadError = true;
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 5000
                });
            } finally {
                this.dataset.loading = false;
            }

            const eventId = this.$route.query.eventId;
            if (eventId) {
                this.openSpecificCollapse(eventId);
            }
        },

        async loadNextGeneralMeetings() {
            try {
                const result = await axios.get('/api/general-meetings/next');

                const data = result.data.content;

                if (data.length) {
                    this.dataset.selectedNextGeneralMeeting = data[0];

                    data.splice(0, 1);

                    this.dataset.nextGeneralMeetings = data;
                    this.openModal();
                }
            } catch (error) {
                console.log('error ', error);
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 5000
                });
            } finally {
                this.dataset.loading = false;
            }
        },

        openModal() {
            // this.modalTitle = title;
            // this.modalMessage = message;
            this.showModal = true;
        },

        closeModal() {
            this.showModal = false;
        },

        async loadText() {
            try {
                const result = await axios.get(`/api/dynamic-infos/keys`, {
                    params: { keys: 'general-meeting-description' }
                });

                this.dataset.description =
                    result.data.content && result.data.content.length > 0
                        ? result.data.content[0].value
                        : '';
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                });
            }
        }
    }
};
</script>

<style scoped>
.assembly-content {
    padding-top: 56px !important;
}

.assembly-intro {
    max-width: 920px;
    margin-bottom: 46px;
    color: #4f6070;
    font-size: 16px;
    line-height: 1.75;
}

.assembly-intro ::v-deep p {
    margin: 0 0 10px;
}

.assembly-directory {
    padding: 30px 0;
    border-top: 1px solid #d8d5ce;
    border-bottom: 1px solid #d8d5ce;
}

.assembly-directory__heading {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 28px;
    margin-bottom: 28px;
}

.assembly-kicker,
.assembly-entry__details-label {
    color: var(--pbb-copper);
    font: 600 10px/1.4 'Manrope', sans-serif;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.assembly-directory h2 {
    margin: 6px 0 0;
    color: var(--pbb-navy);
    font: 500 clamp(25px, 3vw, 38px) / 1.15 'Plus Jakarta Sans', sans-serif;
    letter-spacing: -0.035em;
}

.assembly-directory__heading p {
    margin: 0 0 4px;
    color: var(--pbb-muted);
    font-size: 13px;
}

.assembly-directory__heading p strong {
    color: var(--pbb-navy);
}

.assembly-filters {
    display: grid;
    grid-template-columns: minmax(260px, 1.25fr) minmax(220px, 1fr) 190px;
    gap: 14px;
    align-items: stretch;
}

.assembly-filters__submit {
    height: 40px !important;
}

.assembly-agenda {
    margin-top: 22px;
    border-top: 1px solid #d8d5ce;
}

.assembly-entry {
    border-bottom: 1px solid #d8d5ce;
    transition: background-color 180ms ease;
}

.assembly-entry:hover,
.assembly-entry--open {
    background: rgba(255, 255, 255, 0.55);
}

.assembly-entry__summary {
    display: grid;
    grid-template-columns: 92px minmax(0, 1fr) auto;
    gap: 28px;
    align-items: center;
    width: 100%;
    padding: 28px 20px;
    color: inherit;
    text-align: left;
}

.assembly-date {
    display: grid;
    justify-items: start;
    padding-right: 26px;
    border-right: 1px solid #d8d5ce;
    font-family: 'Manrope', sans-serif;
}

.assembly-date strong {
    color: var(--pbb-navy);
    font-size: 32px;
    font-weight: 500;
    line-height: 1;
}

.assembly-date span {
    margin-top: 5px;
    color: var(--pbb-copper);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
}

.assembly-date small {
    color: var(--pbb-muted);
    font-size: 11px;
}

.assembly-entry__content {
    display: block;
    min-width: 0;
}

.assembly-entry__client {
    display: block;
    margin-bottom: 6px;
    color: var(--pbb-copper);
    font: 600 10px/1.4 'Manrope', sans-serif;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

.assembly-entry__title {
    display: block;
    color: var(--pbb-navy);
    font: 500 clamp(17px, 2vw, 22px) / 1.35 'Plus Jakarta Sans', sans-serif;
    letter-spacing: -0.02em;
}

.assembly-entry__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 22px;
    margin-top: 12px;
    color: var(--pbb-muted);
    font-size: 12px;
}

.assembly-entry__meta span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.assembly-entry__meta .v-icon {
    color: #7d8993;
}

.assembly-entry__aside {
    display: grid;
    justify-items: end;
    gap: 13px;
    min-width: 142px;
}

.assembly-status {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #38614c;
    font: 600 11px/1 'Manrope', sans-serif;
    letter-spacing: 0.05em;
    text-transform: uppercase;
}

.assembly-status::before {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #5f9675;
    content: '';
}

.assembly-status--2,
.assembly-status--3 {
    color: #9e5d34;
}

.assembly-status--2::before,
.assembly-status--3::before {
    background: var(--pbb-copper);
}

.assembly-status--1,
.assembly-status--4 {
    color: #727d87;
}

.assembly-status--1::before,
.assembly-status--4::before {
    background: #8a949c;
}

.assembly-entry__toggle {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: var(--pbb-navy);
    font: 600 12px/1 'Manrope', sans-serif;
}

.assembly-entry__details {
    margin: 0 20px 0 140px;
    padding: 0 0 30px;
    color: #4f6070;
    font-size: 14px;
    line-height: 1.75;
}

.assembly-entry__details::before {
    display: block;
    margin-bottom: 24px;
    border-top: 1px solid #e2dfd8;
    content: '';
}

.assembly-entry__details-label {
    display: block;
    margin-bottom: 10px;
}

.assembly-entry__details ::v-deep p:last-child {
    margin-bottom: 0;
}

.title-page {
    color: var(--v-primary-base);
    font-family: 'Manrope', sans-serif;
    font-size: 30px !important;
    font-style: normal;
    font-weight: 700;
    line-height: 38px;
}

.block-text {
    background-color: white;
    color: black;
    font-family: 'Manrope', sans-serif;
    font-size: 16px !important;
    text-align: justify;
}

.card-body {
    padding: 20px;
}

.clicked-item {
    background-color: white !important;
    color: black !important;
    font-family: 'Manrope', sans-serif;
    font-size: 22px !important;
}

.future-meeting-card {
    background-color: #f5f5f5;
    padding: 10px;
    border-radius: 4px;
    font-size: 0.9em;

    @media (max-width: 600px) {
        font-size: 0.7em;
    }
}

.timeline-scroll-container {
    max-height: 30rem;
    overflow-y: auto;
}

@media (max-width: 850px) {
    .assembly-filters {
        grid-template-columns: 1fr 1fr;
    }

    .assembly-filters__submit {
        grid-column: 1 / -1;
    }

    .assembly-entry__summary {
        grid-template-columns: 72px minmax(0, 1fr);
        gap: 20px;
        padding: 24px 12px;
    }

    .assembly-date {
        padding-right: 18px;
    }

    .assembly-entry__aside {
        grid-column: 2;
        grid-template-columns: 1fr auto;
        justify-items: start;
        width: 100%;
    }

    .assembly-entry__toggle {
        justify-self: end;
    }

    .assembly-entry__details {
        margin-left: 104px;
    }
}

@media (max-width: 600px) {
    .assembly-content {
        padding-top: 40px !important;
    }

    .assembly-intro {
        margin-bottom: 34px;
        font-size: 14px;
    }

    .assembly-directory__heading {
        display: block;
    }

    .assembly-directory__heading p {
        margin-top: 12px;
    }

    .assembly-filters {
        grid-template-columns: 1fr;
    }

    .assembly-filters__submit {
        grid-column: auto;
    }

    .assembly-entry__summary {
        grid-template-columns: 58px minmax(0, 1fr);
        gap: 14px;
        padding: 22px 4px;
    }

    .assembly-date {
        padding-right: 12px;
    }

    .assembly-date strong {
        font-size: 27px;
    }

    .assembly-entry__meta {
        display: grid;
        gap: 7px;
    }

    .assembly-entry__aside {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .assembly-entry__toggle {
        justify-self: start;
    }

    .assembly-entry__details {
        margin: 0 4px 0 76px;
    }
}
</style>
